import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Plus, X, ArrowLeft } from 'lucide-react';
import { DateTimePicker } from '../../components/ui/date-picker';
import { useSite } from '../../context/SiteContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MeetingsEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { activeSiteId } = useSite();
  const [loading, setLoading] = useState(false);
  const [fetchingMeeting, setFetchingMeeting] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [isEditBlocked, setIsEditBlocked] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    meeting_type: 'Safety Committee',
    scheduled_date: '',
    duration: 60,
    location: '',
    is_mandatory: false,
    agenda_items: [],
    attendees: [],
    key_decisions: [],
    status: 'Scheduled',
  });

  useEffect(() => {
    fetchMeeting();
    fetchUsers();
  }, [id, activeSiteId]);

  const fetchMeeting = async () => {
    try {
      setFetchingMeeting(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/meetings/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const meeting = response.data;

      // Format scheduled_date for datetime-local input using LOCAL time components.
      // Using toISOString().slice(0,16) would produce a UTC string without timezone indicator,
      // which new Date() later parses as local time, causing a timezone-offset shift on every save.
      let formattedDate = '';
      if (meeting.scheduled_date) {
        const date = new Date(meeting.scheduled_date);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        formattedDate = `${year}-${month}-${day}T${hours}:${minutes}`;
      }

      // Clean up agenda items to ensure they have proper IDs
      const cleanedAgendaItems = (meeting.agenda_items || []).map(item => ({
        id: item.id || Date.now().toString() + Math.random(),
        topic: item.topic || '',
        duration: item.duration || 10,
        presenter: item.presenter || '',
        notes: item.notes || ''
      }));

      // Clean up attendees to ensure they have proper structure
      console.log('Raw attendees from API:', meeting.attendees);
      const cleanedAttendees = (meeting.attendees || []).map((attendee, index) => {
        const cleaned = {
          id: attendee.id || attendee.user_id || `attendee-${index}`,
          user_id: attendee.user_id,
          user_name: attendee.user_name,
          role: attendee.role,
          attended: attendee.attended || false,
          ...(attendee.attendance_marked_at && {
            attendance_marked_at: attendee.attendance_marked_at
          })
        };
        console.log('Cleaned attendee:', cleaned);
        return cleaned;
      });

      // Block editing if meeting is not in Scheduled status
      if (meeting.status && meeting.status.toLowerCase() !== 'scheduled') {
        setIsEditBlocked(true);
        setError(`Cannot edit this meeting because it is "${meeting.status}". Only scheduled meetings can be edited.`);
        setFetchingMeeting(false);
        return;
      }

      setFormData({
        title: meeting.title || '',
        meeting_type: meeting.meeting_type || 'Safety Committee',
        scheduled_date: formattedDate,
        duration: meeting.duration || 60,
        location: meeting.location || '',
        is_mandatory: meeting.is_mandatory || false,
        agenda_items: cleanedAgendaItems,
        attendees: cleanedAttendees,
        action_items: meeting.action_items || [],
        key_decisions: meeting.key_decisions || [],
        status: meeting.status || 'Scheduled',
        minutes: meeting.minutes || '',
      });

      setFetchingMeeting(false);
    } catch (err) {
      console.error('Failed to fetch meeting:', err);
      setError('Failed to load meeting details');
      setFetchingMeeting(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = activeSiteId ? `${API}/users?site_id=${activeSiteId}` : `${API}/users`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const handleAddAgendaItem = () => {
    setFormData({
      ...formData,
      agenda_items: [
        ...formData.agenda_items,
        { id: Date.now().toString(), topic: '', duration: '', presenter: '', notes: '' }
      ]
    });
  };

  const handleUpdateAgendaItem = (id, field, value) => {
    setFormData({
      ...formData,
      agenda_items: formData.agenda_items.map(item =>
        item.id === id ? { ...item, [field]: value } : item
      )
    });
  };

  const handleRemoveAgendaItem = (id) => {
    setFormData({
      ...formData,
      agenda_items: formData.agenda_items.filter(item => item.id !== id)
    });
  };

  const handleAddAttendee = (user) => {
    if (formData.attendees.find(a => a.user_id === user.id)) return;

    setFormData({
      ...formData,
      attendees: [
        ...formData.attendees,
        {
          id: Date.now().toString(),
          user_id: user.id,
          user_name: user.name,
          role: user.role || 'Employee',
          attended: false
        }
      ]
    });
  };

  const handleRemoveAttendee = (id) => {
    setFormData({
      ...formData,
      attendees: formData.attendees.filter(a => a.id !== id)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate scheduled date is not in the past
    if (formData.scheduled_date) {
      const scheduledDate = new Date(formData.scheduled_date);
      if (scheduledDate < new Date()) {
        setError('Scheduled date and time cannot be in the past. Please select a current or future date and time.');
        setLoading(false);
        return;
      }
    }

    if (formData.is_mandatory && formData.attendees.length === 0) {
      setError('Mandatory attendance is selected. Please add at least one attendee.');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');

      // Format attendees to ensure proper structure and filter out invalid entries
      console.log('Form attendees before formatting:', formData.attendees);
      const formattedAttendees = formData.attendees
        .filter(attendee => {
          const isValid = attendee.user_id && attendee.user_name && attendee.role;
          if (!isValid) {
            console.warn('Invalid attendee filtered out:', attendee);
          }
          return isValid;
        })
        .map(attendee => ({
          id: attendee.id || attendee.user_id,
          user_id: attendee.user_id,
          user_name: attendee.user_name,
          role: attendee.role,
          attended: attendee.attended || false,
          ...(attendee.attendance_marked_at && {
            attendance_marked_at: attendee.attendance_marked_at
          })
        }));
      console.log('Formatted attendees:', formattedAttendees);

      // Format action items to ensure proper structure and filter out invalid entries
      const formattedActionItems = (formData.action_items || [])
        .filter(action => action.description && action.assigned_to && action.assigned_to_name && action.due_date)
        .map(action => ({
          id: action.id || Date.now().toString() + Math.random(),
          description: action.description,
          assigned_to: action.assigned_to,
          assigned_to_name: action.assigned_to_name,
          due_date: action.due_date,
          status: action.status || 'Open',
          ...(action.linked_capa_id && { linked_capa_id: action.linked_capa_id }),
          ...(action.created_at && { created_at: action.created_at })
        }));

      const payload = {
        title: formData.title,
        meeting_type: formData.meeting_type,
        scheduled_date: new Date(formData.scheduled_date).toISOString(),
        duration: formData.duration,
        location: formData.location,
        is_mandatory: formData.is_mandatory,
        agenda_items: formData.agenda_items || [],
        attendees: formattedAttendees,
        action_items: formattedActionItems,
        status: formData.status,
        minutes: formData.minutes || null,
        key_decisions: formData.key_decisions || [],
      };

      console.log('Submitting payload:', JSON.stringify(payload, null, 2));

      await axios.put(`${API}/meetings/${id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      navigate(`/meetings/${id}`);
    } catch (err) {
      console.error('Submit error:', err);
      let errorMsg = 'Failed to update meeting';

      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (Array.isArray(detail)) {
          // Pydantic validation errors
          errorMsg = detail.map(e => `${e.loc.join('.')}: ${e.msg}`).join(', ');
        } else if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (typeof detail === 'object') {
          errorMsg = JSON.stringify(detail);
        }
      } else if (err.message) {
        errorMsg = err.message;
      }

      setError(errorMsg);
      setLoading(false);
    }
  };

  if (fetchingMeeting) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (isEditBlocked) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="outline"
            onClick={() => navigate(`/meetings/${id}`)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Meeting Details
          </Button>
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate(`/meetings/${id}`)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Meeting Details
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Edit Safety Meeting</h1>
          <p className="text-gray-600 mt-1">Update meeting details, agenda, and attendees</p>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <Card className="mb-6">
            <CardContent className="p-6 space-y-6">
              {/* Basic Details */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">Meeting Details</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Meeting Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      placeholder="e.g., Monthly Safety Committee Meeting"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Meeting Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.meeting_type}
                      onChange={(e) => setFormData({ ...formData, meeting_type: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      required
                    >
                      <option value="Safety Committee">Safety Committee</option>
                      <option value="Toolbox Talk">Toolbox Talk</option>
                      <option value="Emergency Drill">Emergency Drill</option>
                      <option value="Management Review">Management Review</option>
                      <option value="Training Session">Training Session</option>
                      <option value="Incident Review">Incident Review</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Scheduled Date & Time <span className="text-red-500">*</span>
                    </label>
                    <DateTimePicker
                      value={formData.scheduled_date}
                      onChange={(value) => setFormData({ ...formData, scheduled_date: value })}
                      placeholder="Select date and time"
                      minDate={new Date().toISOString()}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Duration (minutes) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.duration}
                      onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      min="5"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Location <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      placeholder="e.g., Conference Room A"
                      required
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_mandatory}
                      onChange={(e) => setFormData({ ...formData, is_mandatory: e.target.checked })}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <label className="ml-2 text-sm font-semibold text-gray-700">
                      Mandatory Attendance
                    </label>
                  </div>
                </div>
              </div>

              {/* Agenda Items */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">Agenda</h3>
                  <Button
                    type="button"
                    onClick={handleAddAgendaItem}
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Topic
                  </Button>
                </div>

                {formData.agenda_items.length === 0 ? (
                  <p className="text-gray-500 text-sm">No agenda items yet. Click "Add Topic" to start.</p>
                ) : (
                  <div className="space-y-3">
                    {formData.agenda_items.map((item, index) => (
                      <div key={item.id} className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-start justify-between mb-3">
                          <span className="text-sm font-semibold text-gray-500">Item #{index + 1}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveAgendaItem(item.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div className="md:col-span-2">
                            <input
                              type="text"
                              value={item.topic}
                              onChange={(e) => handleUpdateAgendaItem(item.id, 'topic', e.target.value)}
                              placeholder="Topic"
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                              required
                            />
                          </div>
                          <div>
                            <input
                              type="number"
                              value={item.duration}
                              onChange={(e) => handleUpdateAgendaItem(item.id, 'duration', parseInt(e.target.value))}
                              placeholder="Duration (min)"
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                              min="1"
                              required
                            />
                          </div>
                          <div className="md:col-span-3">
                            <input
                              type="text"
                              value={item.presenter}
                              onChange={(e) => handleUpdateAgendaItem(item.id, 'presenter', e.target.value)}
                              placeholder="Presenter"
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Attendees */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  Attendees {formData.is_mandatory && <span className="text-red-500">*</span>}
                </h3>

                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Add Attendees {formData.is_mandatory && <span className="text-red-500">(Required)</span>}
                  </label>
                  <select
                    onChange={(e) => {
                      const user = users.find(u => u.id === e.target.value);
                      if (user) handleAddAttendee(user);
                      e.target.value = '';
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  >
                    <option value="">Select a user to add...</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>{user.name} ({user.email})</option>
                    ))}
                  </select>
                </div>

                {formData.attendees.length === 0 ? (
                  <p className="text-gray-500 text-sm">No attendees added yet.</p>
                ) : (
                  <div className="space-y-2">
                    {formData.attendees.map(attendee => (
                      <div key={attendee.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-semibold text-gray-900">{attendee.user_name}</p>
                          <p className="text-sm text-gray-600">{attendee.role}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveAttendee(attendee.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Submit Buttons */}
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/meetings/${id}`)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Update Meeting
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MeetingsEdit;
